import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Calendar, History, Edit3, Save, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { client, fetchTransactionsForDate, currencyCodeToSymbolMap, formatCurrency, fetchUtilAccounts, fetchNamedUsers, fetchStores } from '../apiHelper';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import DateTimeInput from '../components/ui/DateTimeInput';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DataTable1 from '../components/ui/DataTable1';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';

const TransactionCorrectionManager = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // --- State ---
    const [user, setUser] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editForm, setEditForm] = useState({
        transaction_id: null,
        transaction_date: '',
        description: '',
        store_id: null,
        customer_member_id: null,
        postings: []
    });

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to manage transactions.", "info");
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);

            // Fetch supporting data for the selectors
            const loadOptions = async () => {
                try {
                    const [fetchedAccounts, fetchedUsers, fetchedStores] = await Promise.all([
                        fetchUtilAccounts(parsedUser),
                        fetchNamedUsers(parsedUser),
                        fetchStores(parsedUser)
                    ]);

                    setAccounts(fetchedAccounts.map(account => ({
                        label: `${account.account_code} - ${account.name}`,
                        value: account.account_id,
                    })));

                    setCustomers(fetchedUsers.map(u => ({
                        label: `${u.user?.first_name || ''} ${u.user?.last_name || ''} (${u.user?.credential?.logon_id || 'N/A'})`,
                        value: u.member_id,
                    })));

                    setStoreOptions(fetchedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id,
                    })));
                } catch (err) {
                    showToast("Error loading form options", "error");
                }
            };
            loadOptions();
        }
    }, [showToast, navigate]);

    // --- Fetching ---
    const loadTransactions = useCallback(async () => {
        setLoading(true);
        try {
            // Using the pattern you described: passing { transaction_date: date }
            const data = await fetchTransactionsForDate({ transaction_date: selectedDate });
            // console.log(data)
            setTransactions(data);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to fetch transactions", "error");
        } finally {
            setLoading(false);
        }
    }, [selectedDate, showToast]);

    useEffect(() => {
        loadTransactions();
    }, [loadTransactions]);

    const handleVoidTransaction = async (tx) => {
        console.log("✅ Void clicked for transaction:", tx); // Temporary debug

        if (!tx) {
            console.error("No transaction data passed");
            return;
        }

        try {
            const response = await client.fetchData(`accounting/void-transaction/${tx.transaction_id}`);
            console.log(response)
            showToast(response.msg || `Successfully voided transaction ${tx.ref}`, 'success');
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to void transaction ${tx.ref}`;
            showToast(errorMessage, 'error');
        }
    }

    // --- Edit Logic ---
    const handleOpenEdit = (tx) => {
        // console.log("✅ Edit clicked for transaction:", tx); // Temporary debug

        if (!tx) {
            console.error("No transaction data passed");
            return;
        }

        setEditForm({
            transaction_id: tx.transaction_id,
            transaction_date: tx.transaction_date?.substring(0, 16) || '',
            description: tx.description || '',
            store_id: tx.store_id,
            customer_member_id: tx.customer?.member_id || null,
            postings: tx.postings?.map(p => ({
                posting_id: p.posting_id,
                account_id: p.account_id,
                amount: p.amount,
                posting_type: p.posting_type
            })) || []
        });
        setIsEditModalOpen(true);
    };

    const handlePostingChange = (index, field, value) => {
        const updated = [...editForm.postings];
        updated[index] = { ...updated[index], [field]: value };
        setEditForm({ ...editForm, postings: updated });
    };

    const handleSubmitEdit = async () => {
        setSubmitting(true);
        try {
            // Payload keys match your yesterdaystransactions.json sample exactly
            const payload = {
                transaction_id: editForm.transaction_id,
                transaction_date: editForm.transaction_date,
                description: editForm.description,
                customer_id: editForm.customer_member_id,
                store_id: editForm.store_id,
                postings: editForm.postings.map(p => ({
                    posting_id: p.posting_id,
                    account_id: p.account_id,
                    amount: parseFloat(p.amount),
                    posting_type: p.posting_type
                }))
            };
            // console.log(payload)
            const response = await client.postData('/accounting/edit-transaction', payload);
            showToast(response.msg || "Transaction updated", "success");
            setIsEditModalOpen(false);
            loadTransactions();
        } catch (err) {
            showToast(err.response?.data?.detail || "Update failed", "error");
        } finally { setSubmitting(false); }
    };

    // --- Table Configuration ---
    const columns = [
        {
            key: 'ref',
            label: 'Reference',
            render: (val, row) => (
                <div className="flex flex-col">
                    <span className="font-mono text-xs font-bold text-purple-600">{val}</span>
                    <span className="text-[10px] text-gray-400">{row.branch_name}</span>
                </div>
            )
        },
        {
            key: 'transaction_date',
            label: 'Date',
            render: (val) => (
                <span className="text-sm text-gray-800">{new Date(val).toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'short'})}</span>
            )
        },
        {
            key: 'customer',
            label: 'Customer',
            render: (val, row) => {
                const parts = [val?.first_name, val?.middle_name, val?.last_name]
                const fullName = parts.map(name => name?.trim()).filter(Boolean).join(' ');
                return (
                    <span className="text-sm text-gray-700">{fullName || 'Null'}</span>
                )
            }
        },
        {
            key: 'description',
            label: 'Description',
            render: (val) => <span className="text-sm text-gray-700">{val || 'No description'}</span>
        },
        {
            key: 'postings',
            label: 'Postings (Debit/Credit)',
            render: (postings, row) => (
                <div className="space-y-1">
                    {postings.map((p, idx) => (
                        <div key={idx} className="flex justify-between gap-4 text-xs border-b border-gray-50 pb-1">
                            <span className={p.posting_type === 'debit' ? 'text-blue-600 font-medium' : 'text-amber-600 font-medium'}>
                                {p.account_name}
                            </span>
                            <span className="font-mono">
                                {formatCurrency(Number(p.amount || 0), 2, row.currency_code, currencyCodeToSymbolMap)}
                            </span>
                        </div>
                    ))}
                </div>
            )
        },
        {
            key: 'creator',
            label: 'Created By',
            render: (creator) => (
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold">
                        {creator.first_name[0]}{creator.last_name[0]}
                    </div>
                    <span className="text-xs">{creator.first_name} {creator.last_name}</span>
                </div>
            )
        }
    ];

    const rowActions = [
        {
            label: 'Edit / Correct',
            key: 'edit',
            icon: <Edit3 size={14} />,
        },
        {
            label: 'Void Transaction',
            key: 'void',
            icon: <Ban size={14} />,
        },
    ];

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <History className="text-purple-600" />
                            Transaction Corrections
                        </h1>
                        <p className="text-sm text-gray-500">Search and modify transaction information.</p>
                    </div>

                    <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border">
                        <Calendar size={18} className="text-gray-400 ml-2" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="bg-transparent border-none text-sm focus:ring-0 outline-none"
                        />
                        <Button
                            variant="primary"
                            onClick={loadTransactions}
                            loading={loading}
                            className="py-1.5 px-3"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        </Button>
                    </div>
                </div>

                {/* Main Data Table */}
                <DataTable1
                    columns={columns}
                    data={transactions}
                    loading={loading}
                    enableSearch={true}
                    rowActions={rowActions}
                    onAction={(key, row) => {
                        if (key === 'edit') handleOpenEdit(row);
                        else if (key === 'void') handleVoidTransaction(row);
                    }}
                />

                {/* Reversal Confirmation Modal */}
                <Modal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    title="Edit / Correct Transaction"
                    size="wide"
                >
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DateTimeInput
                                label="Transaction Date"
                                value={editForm.transaction_date}
                                onChange={(e) => setEditForm({ ...editForm, transaction_date: e.target.value })}
                            />
                            <SearchableSelectBox
                                label="Store"
                                options={storeOptions}
                                value={editForm.store_id}
                                onChange={(e) => setEditForm({ ...editForm, store_id: e.target.value })}
                            />
                        </div>

                        <Input
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        />

                        <SearchableSelectBox
                            label="Customer"
                            options={customers}
                            value={editForm.customer_member_id}
                            onChange={(e) => setEditForm({ ...editForm, customer_member_id: e.target.value })}
                        />

                        <div className="pt-4 border-t">
                            <h3 className="text-sm font-bold mb-4">Postings</h3>
                            {editForm.postings.map((post, idx) => (
                                <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg mb-2 items-end">
                                    <div className="flex-1">
                                        <SearchableSelectBox
                                            label="Account"
                                            options={accounts}
                                            value={post.account_id}
                                            onChange={(e) => handlePostingChange(idx, 'account_id', e.target.value)}
                                        />
                                    </div>
                                    <div className="w-32">
                                        <Select
                                            label="Type"
                                            options={[{ label: 'Debit', value: 'debit' }, { label: 'Credit', value: 'credit' }]}
                                            value={post.posting_type}
                                            onChange={(e) => handlePostingChange(idx, 'posting_type', e.target.value)}
                                        />
                                    </div>
                                    <div className="w-40">
                                        <Input
                                            label="Amount"
                                            type="number"
                                            disabled
                                            value={post.amount}
                                            onChange={(e) => handlePostingChange(idx, 'amount', e.target.value)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3 justify-end pt-6 border-t mt-6">
                            <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                            <Button className="flex items-center justify-center" variant="primary" onClick={handleSubmitEdit} loading={submitting}>
                                <Save size={18} className="mr-2" /> Save Corrections
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default TransactionCorrectionManager;